import { useQuery } from '@apollo/client/react';
import { differenceInCalendarDays, parseISO } from 'date-fns';

import type { ListingComparablesQuery } from '@graphql/__generated__/graphql';
import { composePropertyType } from '@modules/_shared/utils/propertyTypes';
import {
  LISTING_BENCHMARK_SUBJECT,
  LISTING_COMPARABLES,
} from '@modules/listings/listingActivityQueries';
import { useLocale } from '@shared/hooks/locale';

import {
  excludeSubjectListing,
  getSubjectReferenceIds,
} from './excludeSubjectListing';

const COHORT_LIMIT = 60;
const BENCHMARK_COUNTRIES = ['CH', 'ES', 'FR', 'IT'] as const;

// One comparable row (scrapers cohort wrapper: distance + listing).
export type ComparableRow = NonNullable<
  NonNullable<
    ListingComparablesQuery['ch_get_similar_listings_cohorts']
  >[number]
>;

export type ListingComparables =
  | { state: 'loading' }
  | { state: 'error' }
  // Unsupported country, missing coordinates or property type — we can't query
  // the scrapers comparable set at all.
  | { state: 'unavailable' }
  | {
      state: 'ready';
      // Still-live comparables, most recently listed first.
      online: ComparableRow[];
      // Comparables that went offline, most recently offline first.
      offline: ComparableRow[];
      // Median days the online comparables have been live so far.
      onlineDomMedian: number | null;
      // Median days the offline comparables stayed online before going offline.
      offlineDurationMedian: number | null;
      onlinePpsmMedian: number | null;
      offlinePpsmMedian: number | null;
    };

const median = (values: number[]): number | null => {
  if (values.length === 0) {
    return null;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
};

const onlineDays = (row: ComparableRow, now: Date): number | null => {
  const onlineStr = row.listing?.valid_from ?? row.listing?.created_at;
  if (onlineStr == null) {
    return null;
  }
  const days = differenceInCalendarDays(now, parseISO(onlineStr));
  return days >= 0 ? days : null;
};

const lifespanDays = (row: ComparableRow): number | null => {
  const onlineStr = row.listing?.valid_from ?? row.listing?.created_at;
  const offlineStr = row.listing?.offline_since;
  if (onlineStr == null || offlineStr == null) {
    return null;
  }
  const days = differenceInCalendarDays(
    parseISO(offlineStr),
    parseISO(onlineStr),
  );
  return days >= 0 ? days : null;
};

const ppsmValues = (rows: ComparableRow[]): number[] =>
  rows
    .map(row => row.listing?.computed_price_per_sqm)
    .filter((value): value is number => value != null && value > 0);

// Fetches real comparable listings from the scrapers schema
// (`*_get_similar_listings_cohorts`, same function the CMA uses), split into
// online and offline sets with the medians shown above each carousel.
export const useListingComparables = (lotId: string): ListingComparables => {
  const { countryCode } = useLocale();

  const {
    data: subjectData,
    loading: subjectLoading,
    error: subjectError,
  } = useQuery(LISTING_BENCHMARK_SUBJECT, { variables: { lotId } });

  const property = subjectData?.lots_by_pk?.property;
  const salePrice = subjectData?.lots_by_pk?.sale_price ?? null;
  const mainType = property?.property_type?.main_type ?? null;

  const supportedCountry = (BENCHMARK_COUNTRIES as readonly string[]).includes(
    countryCode,
  );
  const canQuery =
    supportedCountry &&
    property?.lat != null &&
    property?.lng != null &&
    mainType != null;

  // The scrapers cohort function expects the composed property type (e.g.
  // 'HOUSE', 'APPT') — the same value the CMA passes — and an integer room
  // count. Passing a fractional `number_of_rooms` (4.5, 5.5 …) into the `Int`
  // GraphQL variable throws a server-side validation error that fails the whole
  // query, so it must be rounded.
  const propertyMainType = composePropertyType(
    mainType,
    property?.property_type?.name ?? null,
  );
  const numberOfRooms =
    property?.number_of_rooms != null
      ? Math.round(property.number_of_rooms)
      : null;

  const {
    data: cohortData,
    loading: cohortLoading,
    error: cohortError,
  } = useQuery(LISTING_COMPARABLES, {
    skip: !canQuery,
    context: { clientName: 'scrapers' },
    variables: {
      lat: property?.lat ?? null,
      lng: property?.lng ?? null,
      number_of_rooms: numberOfRooms,
      living_surface: property?.living_surface ?? null,
      built_surface: property?.built_surface ?? null,
      usable_surface: property?.usable_surface ?? null,
      sale_price: salePrice,
      property_main_type: propertyMainType,
      limit: COHORT_LIMIT,
      ch: countryCode === 'CH',
      es: countryCode === 'ES',
      fr: countryCode === 'FR',
      it: countryCode === 'IT',
    },
  });

  if (subjectLoading || (canQuery && cohortLoading)) {
    return { state: 'loading' };
  }

  if (subjectError != null || cohortError != null) {
    return { state: 'error' };
  }

  if (!canQuery) {
    return { state: 'unavailable' };
  }

  const cohort =
    cohortData?.ch_get_similar_listings_cohorts ??
    cohortData?.es_get_similar_listings_cohorts ??
    cohortData?.fr_get_similar_listings_cohorts ??
    cohortData?.it_get_similar_listings_cohorts ??
    [];

  const rows = excludeSubjectListing(
    cohort.filter((row): row is ComparableRow => row?.listing != null),
    getSubjectReferenceIds(subjectData?.lots_by_pk),
  );

  const now = new Date();
  const online = rows
    .filter(row => row.listing?.offline_since == null)
    .sort((a, b) => {
      const aDate = a.listing?.valid_from ?? a.listing?.created_at ?? '';
      const bDate = b.listing?.valid_from ?? b.listing?.created_at ?? '';
      return bDate.localeCompare(aDate);
    });
  const offline = rows
    .filter(row => row.listing?.offline_since != null)
    .sort((a, b) =>
      (b.listing?.offline_since ?? '').localeCompare(
        a.listing?.offline_since ?? '',
      ),
    );

  const onlineDomValues = online
    .map(row => onlineDays(row, now))
    .filter((value): value is number => value != null);
  const offlineDurationValues = offline
    .map(row => lifespanDays(row))
    .filter((value): value is number => value != null);

  return {
    state: 'ready',
    online,
    offline,
    onlineDomMedian: median(onlineDomValues),
    offlineDurationMedian: median(offlineDurationValues),
    onlinePpsmMedian: median(ppsmValues(online)),
    offlinePpsmMedian: median(ppsmValues(offline)),
  };
};
